<template>
    <div class="pie-chart">
        <div
            ref="graphicParent"
            class="vis-wrapper"
        >
            <div
                :id="displayDiv"
                ref="vis"
                class="chart-display-div"
            />
        </div>
    </div>
</template>
<script>

import vegaChartHelpers from '@foundryspatial/mixin-vega-chart-helpers';

export default {
    components: {
    },
    mixins: [
        vegaChartHelpers,
    ],
    props: {
        /**
         * The chart info is expected to be in the following format:
         *  {
         *      nameKey: 'title',
         *      percentageKey: 'percentage',
         *      colorKey: 'color',
         *      data: [
         *          {
         *              title: 'Section A',
         *              percentage: 85,
         *              color: '#33284a',
         *          },
         *          [...]
         *      ],
         *  }
         *
         * The keys used for certain values must be specified, and all data must be in an array that takes the above
         * format.
         */
        chartInfo: {
            type: Object,
            required: true,
        },
        /**
         * The following options can be provided:
         *
         * - cornerRadius              (number)
         *      -> the corner radius to set on the corners of each pie chart section
         *      -> inner corners are only rounded if the innerRadius is set
         *
         * - endAngle                  (number)
         *      -> the angle the pie chart should end at
         *
         * - innerRadius               (number)
         *      -> inner circle radius of the pie chart
         *      -> allows the creation of a "donut" chart
         *
         * - padAngle                  (number)
         *      -> how much to pad each pie chart section by
         *
         * - roundToDecimalPlaces      (number)
         *      -> the number of decimal places to round values to
         *
         * - sort                      (boolean)
         *      -> whether or not the pie chart sections should be sorted by percentage
         *
         * - startAngle                (number)
         *      -> the angle the pie chart should start at
         */
        providedOptions: {
            type: Object,
            required: false,
            default: () => {},
        },
    },
    data() {
        return {
            vegaView: null,
            vegaSpec: {
                $schema: 'https://vega.github.io/schema/vega/v5.json',
                autosize: { type: 'fit', contains: 'padding' },

                data: [
                    {
                        name: 'chart',
                        transform: [
                            {
                                type: 'pie',
                                field: 'percentage',
                            },
                        ],
                    },
                ],

                signals: [
                    {
                        name: 'width',
                        update: 'containerSize()[0]',
                        on: [
                            {
                                events: { source: 'window', type: 'resize', throttle: 50 },
                                update: 'containerSize()[0]',
                            },
                        ],
                    },
                    {
                        name: 'height',
                        update: 'containerSize()[0]',
                        on: [
                            {
                                events: { source: 'window', type: 'resize', throttle: 50 },
                                update: 'containerSize()[0]',
                            },
                        ],
                    },
                ],

                scales: [
                    {
                        name: 'colors',
                        type: 'ordinal',
                        domain: { data: 'chart', field: 'color' },
                        range: [],
                    },
                ],

                marks: [
                    {
                        type: 'arc',
                        from: { data: 'chart' },
                        encode: {
                            enter: {
                                fill: { scale: 'colors', field: 'color' },
                                x: { signal: 'width / 2' },
                                y: { signal: 'height / 2' },
                                tooltip: {
                                    signal: "datum.name + ': ' + datum.tooltipPercentage + '%'",
                                },
                            },
                            update: {
                                startAngle: { field: 'startAngle' },
                                endAngle: { field: 'endAngle' },
                                outerRadius: { signal: 'width / 2' },
                            },
                        },
                    },
                ],
            },
        };
    },
    computed: {
        options() {
            return {
                cornerRadius: 0,
                endAngle: 6.29,
                innerRadius: 0,
                padAngle: 0,
                roundToDecimalPlaces: '2',
                sort: true,
                startAngle: 0,
                ...this.providedOptions,
            };
        },
    },
    watch: {
        data() {
            this.handleDataChange();
        },
        providedOptions() {
            this.handleOptionsChange();
        },
    },
    mounted() {
        this.handleDataChange();
        this.handleOptionsChange();
    },
    methods: {
        /**
         * Handle the chart info prop changing.
         */
        handleDataChange() {
            const {
                nameKey,
                percentageKey,
                colorKey,
                data,
            } = this.chartInfo;

            const processedData = [];
            const colors = [];

            data.forEach((point) => {
                // put data into the correct format for the chart
                processedData.push({
                    name: point[nameKey],
                    percentage: point[percentageKey],
                    tooltipPercentage: this.prepareNumberForTooltip(point[percentageKey]),
                    color: point[colorKey],
                });

                // collect all chart colors
                colors.push(point[colorKey]);
            });

            // set vegaSpec data to our newly processed data
            this.vegaSpec.data[0].values = processedData;
            // set the range of the color scale
            this.vegaSpec.scales[0].range = colors;

            this.render();
        },

        handleOptionsChange() {
            // apply data transform options
            this.vegaSpec.data[0].transform[0] = {
                ...this.vegaSpec.data[0].transform[0],
                startAngle: this.options.startAngle,
                endAngle: this.options.endAngle,
                sort: this.options.sort,
            };

            // apply mark options
            this.vegaSpec.marks[0].encode.update = {
                ...this.vegaSpec.marks[0].encode.update,
                padAngle: { value: this.options.padAngle },
                innerRadius: { value: this.options.innerRadius },
                cornerRadius: { value: this.options.cornerRadius },
            };

            this.render();
        },
    },
};
</script>
<style lang="scss">
.pie-chart {
    .vis-wrapper {
        overflow: hidden;
        width: 100%;
        height: 100%;

        .chart-display-div {
            height: 100%;
            width: 100%;
        }
    }
}
</style>
